import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Dropdown,
  Flex,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Typography
} from 'antd'
import type { FormInstance } from 'antd'
import { createStyles } from 'antd-style'
import { ChevronDown, Key, PowerOff, RotateCw } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { MonoValue, SectionLabel } from 'src/components'

import {
  ALLOWED_ORIGINS_PLACEHOLDER,
  CLIENT_TYPE_POLICY,
  POST_LOGOUT_URI_PLACEHOLDER,
  REDIRECT_URI_PLACEHOLDER
} from './constants'
import type {
  ClientType,
  GrantType,
  OidcClient,
  OidcConfigFormValues
} from './types'
import {
  listFromMultiline,
  multilineFromList,
  validateOriginList,
  validateUriList
} from './utils'

type Props = {
  applicationName: string
  client: OidcClient
  form: FormInstance<OidcConfigFormValues>
  isSaving: boolean
  isRotating: boolean
  isRevoking: boolean
  onSubmit: (values: OidcConfigFormValues) => void
  onRotateSecret: () => void
  onRevoke: () => void
}

const useStyles = createStyles(({ token }) => ({
  identityRow: {
    padding: 14,
    borderRadius: token.borderRadius,
    border: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorFillQuaternary
  }
}))

export const OidcConfigurationCard = ({
  applicationName,
  client,
  form,
  isSaving,
  isRotating,
  isRevoking,
  onSubmit,
  onRotateSecret,
  onRevoke
}: Props) => {
  const { t } = useTranslation()
  const { styles } = useStyles()

  const existingClientType: ClientType =
    (client.client_type as ClientType | undefined) ?? 'confidential'
  // Watch the in-form value so policy-driven constraints react immediately
  // when the admin swaps client_type without saving yet.
  const watchedClientType =
    Form.useWatch<ClientType>('client_type', form) ?? existingClientType
  const isPublic = watchedClientType === 'public'
  const policy = CLIENT_TYPE_POLICY[watchedClientType]

  useEffect(() => {
    form.setFieldsValue({
      algorithm: (client.algorithm as '' | 'RS256' | 'HS256') ?? '',
      allow_client_credentials: client.allow_client_credentials ?? true,
      allowed_origins: multilineFromList(client.allowed_origins),
      client_type: existingClientType,
      grant_type: (client.grant_type as GrantType) ?? 'client-credentials',
      name: client.name ?? applicationName,
      pkce_required: client.pkce_required ?? true,
      post_logout_redirect_uris: multilineFromList(
        client.post_logout_redirect_uris
      ),
      redirect_uris: multilineFromList(client.redirect_uris)
    })
    // `client.id` keeps the effect scoped to the loaded client and avoids
    // resetting on every render of the parent.
  }, [applicationName, client.id])

  // When the admin flips client_type mid-edit, snap the dependent fields
  // into the new policy so the form preview matches what the backend will
  // accept on save.
  useEffect(() => {
    const current = form.getFieldsValue([
      'pkce_required',
      'allow_client_credentials',
      'algorithm',
      'grant_type'
    ])
    const patch: Partial<OidcConfigFormValues> = {}
    if (
      policy.pkceLocked &&
      current.pkce_required !== policy.defaultPkceRequired
    ) {
      patch.pkce_required = policy.defaultPkceRequired
    }
    if (
      policy.allowClientCredentialsLocked &&
      current.allow_client_credentials !== policy.defaultAllowClientCredentials
    ) {
      patch.allow_client_credentials = policy.defaultAllowClientCredentials
    }
    const grantAllowed = policy.allowedGrants.some(
      (g) => g.value === current.grant_type
    )
    if (!grantAllowed) {
      patch.grant_type = policy.defaultGrant
    }
    const algoAllowed = policy.allowedAlgorithms.some(
      (a) => a.value === current.algorithm
    )
    if (!algoAllowed) {
      patch.algorithm = ''
    }
    if (Object.keys(patch).length > 0) {
      form.setFieldsValue(patch)
    }
    // When the user switches to a type whose policy now requires
    // redirect_uris, surface the inline error immediately so it doesn't
    // surprise them at save time. `validateFields` ignores empty optional
    // fields, so this is safe when redirect_uris isn't required.
    if (policy.redirectUrisRequired) {
      void form.validateFields(['redirect_uris']).catch(() => {})
    }
  }, [watchedClientType])

  const switchExtra =
    watchedClientType !== existingClientType
      ? watchedClientType === 'public'
        ? t(
            'oidc.client_type_switch_to_public',
            'Saving will revoke the current client_secret and switch to PKCE-only. Make sure Redirect URIs is filled before saving — it is required for Public clients.'
          )
        : t(
            'oidc.client_type_switch_to_confidential',
            'Saving will issue a new client_secret (shown once).'
          )
      : undefined

  return (
    <Card
      variant="outlined"
      size="small"
      title={t('oidc.configuration', 'OIDC configuration')}
      extra={
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'rotate',
                icon: <RotateCw size={14} />,
                disabled: isPublic,
                label: (
                  <Popconfirm
                    title={t(
                      'oidc.rotate_confirm_title',
                      'Rotate client secret?'
                    )}
                    description={t(
                      'oidc.rotate_confirm_description',
                      'Any deployment still using the current secret will stop working until updated. The new secret will be shown once.'
                    )}
                    okText={t('oidc.rotate_confirm_ok', 'Rotate')}
                    okButtonProps={{ danger: true, loading: isRotating }}
                    onConfirm={onRotateSecret}
                  >
                    <span>{t('oidc.rotate_secret', 'Rotate secret')}</span>
                  </Popconfirm>
                )
              },
              { type: 'divider' },
              {
                key: 'revoke',
                icon: <PowerOff size={14} />,
                danger: true,
                label: (
                  <Popconfirm
                    title={t('oidc.disable_confirm_title', 'Disable OIDC?')}
                    description={t(
                      'oidc.disable_confirm_description',
                      'Existing tokens will be revoked. You can re-enable it later.'
                    )}
                    okText={t('oidc.disable', 'Disable')}
                    okButtonProps={{ danger: true, loading: isRevoking }}
                    onConfirm={onRevoke}
                  >
                    <span>{t('oidc.disable', 'Disable')}</span>
                  </Popconfirm>
                )
              }
            ]
          }}
        >
          <Button size="small">
            <Flex align="center" gap={6}>
              <Key size={14} />
              <span>{t('oidc.actions', 'Actions')}</span>
              <ChevronDown size={14} />
            </Flex>
          </Button>
        </Dropdown>
      }
    >
      <Flex vertical gap={16}>
        {/* Identity row — client_id + type chip, always visible */}
        {client.client_id ? (
          <div className={styles.identityRow}>
            <Row gutter={[14, 10]} align="middle">
              <Col xs={24} md={14}>
                <Flex vertical gap={4}>
                  <SectionLabel>
                    {t('oidc.client_id', 'Client ID')}
                  </SectionLabel>
                  <MonoValue ellipsis>{client.client_id}</MonoValue>
                </Flex>
              </Col>
              <Col xs={24} md={10}>
                <Flex vertical gap={4}>
                  <SectionLabel>
                    {t('oidc.client_type', 'Client type')}
                  </SectionLabel>
                  <Flex align="center" gap={8}>
                    <Badge
                      color={isPublic ? '#2563EB' : '#7C3AED'}
                      text={
                        isPublic
                          ? t('oidc.client_type_public_short', 'Public')
                          : t(
                              'oidc.client_type_confidential_short',
                              'Confidential'
                            )
                      }
                    />
                  </Flex>
                </Flex>
              </Col>
            </Row>
          </div>
        ) : null}

        {isPublic ? (
          <Alert
            type="info"
            showIcon
            title={t(
              'oidc.public_client_hint',
              'Public client — only the Client ID is needed. The token endpoint accepts requests without an Authorization header; PKCE is required.'
            )}
          />
        ) : null}

        <Form<OidcConfigFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={onSubmit}
        >
          <Row gutter={[14, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('name', 'Name')}
                name="name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('required_field_error', 'This field is required')
                  }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('oidc.client_type', 'Client type')}
                name="client_type"
                rules={[{ required: true }]}
                extra={switchExtra}
              >
                <Select
                  options={[
                    {
                      label: t('oidc.client_type_public_short', 'Public'),
                      value: 'public'
                    },
                    {
                      label: t(
                        'oidc.client_type_confidential_short',
                        'Confidential'
                      ),
                      value: 'confidential'
                    }
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t('oidc.grant_type', 'Grant type')}
                name="grant_type"
                rules={[{ required: true }]}
              >
                <Select options={[...policy.allowedGrants]} />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t('oidc.algorithm', 'Algorithm')}
                name="algorithm"
              >
                <Select options={[...policy.allowedAlgorithms]} />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '4px 0 16px' }} />

          <Row gutter={[14, 0]}>
            <Col span={24}>
              <Form.Item
                label={t('oidc.redirect_uris', 'Redirect URIs')}
                name="redirect_uris"
                required={policy.redirectUrisRequired}
                extra={t(
                  'oidc.enter_one_uri_per_line',
                  'Enter one URI per line'
                )}
                rules={[
                  {
                    validator: (_rule, value: string) => {
                      const lines = listFromMultiline(value)
                      if (policy.redirectUrisRequired && lines.length === 0) {
                        return Promise.reject(
                          new Error(
                            t(
                              'oidc.setup.redirect_uris_required',
                              'Add at least one redirect URI.'
                            )
                          )
                        )
                      }
                      const result = validateUriList(value)
                      if (!result.ok) {
                        return Promise.reject(
                          new Error(
                            t(
                              'oidc.setup.redirect_uris_invalid',
                              'These lines do not look like valid URIs: {{list}}',
                              { list: result.invalid.join(', ') }
                            )
                          )
                        )
                      }
                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder={REDIRECT_URI_PLACEHOLDER}
                  autoComplete="off"
                  spellCheck={false}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t(
                  'oidc.post_logout_redirect_uris',
                  'Post-logout redirect URIs'
                )}
                name="post_logout_redirect_uris"
                extra={t(
                  'oidc.enter_one_uri_per_line',
                  'Enter one URI per line'
                )}
                rules={[
                  {
                    validator: (_rule, value: string) => {
                      const result = validateUriList(value)
                      if (!result.ok) {
                        return Promise.reject(
                          new Error(
                            t(
                              'oidc.setup.redirect_uris_invalid',
                              'These lines do not look like valid URIs: {{list}}',
                              { list: result.invalid.join(', ') }
                            )
                          )
                        )
                      }
                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder={POST_LOGOUT_URI_PLACEHOLDER}
                  autoComplete="off"
                  spellCheck={false}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label={t('oidc.allowed_origins', 'Allowed origins')}
                name="allowed_origins"
                extra={t(
                  'oidc.allowed_origins_extra',
                  'CORS origins (scheme + host + optional port, no path) allowed to call OIDC endpoints. One per line.'
                )}
                rules={[
                  {
                    validator: (_rule, value: string) => {
                      const result = validateOriginList(value)
                      if (!result.ok) {
                        return Promise.reject(
                          new Error(
                            t(
                              'oidc.allowed_origins_invalid',
                              'These lines are not valid origins (no path/query allowed): {{list}}',
                              { list: result.invalid.join(', ') }
                            )
                          )
                        )
                      }

                      return Promise.resolve()
                    }
                  }
                ]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder={ALLOWED_ORIGINS_PLACEHOLDER}
                  autoComplete="off"
                  spellCheck={false}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '4px 0 16px' }} />

          <Row gutter={[14, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="pkce_required"
                valuePropName="checked"
                extra={
                  isPublic
                    ? t(
                        'oidc.pkce_forced_for_public',
                        'Required for public clients and cannot be disabled.'
                      )
                    : t(
                        'oidc.require_pkce_extra',
                        'Recommended. Disable only for legacy clients that cannot generate a code_verifier.'
                      )
                }
              >
                <Checkbox disabled={policy.pkceLocked}>
                  {t('oidc.require_pkce', 'Require PKCE')}
                </Checkbox>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="allow_client_credentials"
                valuePropName="checked"
                extra={
                  policy.allowClientCredentialsLocked
                    ? t(
                        'oidc.allow_client_credentials_disabled',
                        'Not available for public clients.'
                      )
                    : undefined
                }
              >
                <Checkbox disabled={policy.allowClientCredentialsLocked}>
                  {t(
                    'oidc.allow_client_credentials',
                    'Allow client credentials'
                  )}
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Flex justify="flex-end" gap={8}>
            <Button
              size="small"
              onClick={() => form.resetFields()}
              disabled={isSaving}
            >
              {t('workspace.apps.settingsTab.reset', 'Reset')}
            </Button>
            <Button
              size="small"
              type="primary"
              htmlType="submit"
              loading={isSaving}
            >
              {t('button.save', 'Save')}
            </Button>
          </Flex>
        </Form>

        {isRotating ? (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {t('oidc.rotating_secret', 'Rotating client secret…')}
          </Typography.Text>
        ) : null}
      </Flex>
    </Card>
  )
}
